import React, { useMemo } from 'react';

import get from 'lodash/fp/get';
import uniqueId from 'lodash/uniqueId';

import { classnames, margin } from '@lumapps/classnames';
import { CustomRadioList } from '@lumapps/designer-widget-settings/components/CustomRadioList';
import { ViewModeBlock } from '@lumapps/designer-widget-settings/components/ViewModeBlock';
import { VIEW_MODE_LIST_VARIANTS } from '@lumapps/designer-widget-settings/constants';
import { WIDGET_SETTINGS } from '@lumapps/designer-widget-settings/keys';
import { ViewModeType } from '@lumapps/designer-widget-settings/types';
import { Divider, Heading } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';
import { WIDGET_BASE } from '@lumapps/widget-base/keys';

import { VIEW_MODE_PLAYLIST_VARIANTS } from '../../../constants';
import { PlaylistViewModeVariant, PlaylistViewPositionVariant, WidgetPlaylistProperties } from '../../../types';

export type PlaylistVariant = Pick<WidgetPlaylistProperties, 'viewModeVariant' | 'viewPositionVariant'>;

export interface PlaylistVariantsProps {
    className?: string;
    properties: PlaylistVariant;
    onChange: (properties: PlaylistVariant) => void;
}

const CLASSNAME = 'playlist-variants';

/**
 *
 * @param PlaylistVariantsProps
 * @returns PlaylistVariants
 */
export const PlaylistVariants = ({ className, properties, onChange }: PlaylistVariantsProps) => {
    const { translateKey } = useTranslate();
    const viewVariantLabelId = useMemo(() => uniqueId(`${CLASSNAME}-view-variant-label-`), []);
    const viewPositionVariantLabelId = useMemo(() => uniqueId(`${CLASSNAME}-view-positionvariant-label-`), []);

    const selectedViewModeVariant =
        VIEW_MODE_LIST_VARIANTS.find((view) => view.value === properties.viewModeVariant) || VIEW_MODE_LIST_VARIANTS[0];

    const selectedViewPositionVariant =
        VIEW_MODE_PLAYLIST_VARIANTS.find((view) => view.value === properties.viewPositionVariant) ||
        VIEW_MODE_PLAYLIST_VARIANTS[0];

    return (
        <div className={classnames(className, CLASSNAME)}>
            {/*
             * View MODE variant selector — controls the overall layout of the playlist widget:
             * - "GRID": old version of the playlist without player / "SPLIT": modern version similar to the playlist page with player
             */}
            <Heading as="h4" typography="subtitle1" className={margin('bottom', 'huge')}>
                {translateKey(WIDGET_SETTINGS.VIEW_MODE)}
            </Heading>

            <CustomRadioList
                aria-labelledby={viewPositionVariantLabelId}
                items={VIEW_MODE_PLAYLIST_VARIANTS.map((viewMode) => {
                    return { viewMode };
                })}
                dataScope={CLASSNAME}
                itemRenderer={ViewModeBlock}
                itemKeySelector={get('viewMode.value')}
                selectedItem={{ viewMode: selectedViewPositionVariant }}
                setSelectedItem={(item: { viewMode: ViewModeType<PlaylistViewPositionVariant> }) => {
                    onChange({
                        ...properties,
                        viewPositionVariant: item.viewMode.value,
                    });
                }}
            />

            <Divider />

            {/*
             * View VARIANT selector — controls the overall layout of the playlist widget:
             * - "GROUPED": groups related elements together / "UNGROUPED": separates elements into distinct sections
             */}
            <Heading as="h4" typography="subtitle1" className={margin('bottom', 'huge')}>
                {translateKey(WIDGET_BASE.SETTINGS_VARIANTS)}
            </Heading>

            <CustomRadioList
                aria-labelledby={viewVariantLabelId}
                items={VIEW_MODE_LIST_VARIANTS.map((viewMode) => {
                    return { viewMode };
                })}
                dataScope={CLASSNAME}
                itemRenderer={ViewModeBlock}
                itemKeySelector={get('viewMode.value')}
                selectedItem={{ viewMode: selectedViewModeVariant }}
                setSelectedItem={(item: { viewMode: ViewModeType<PlaylistViewModeVariant> }) =>
                    onChange({
                        ...properties,
                        viewModeVariant: item.viewMode.value,
                    })
                }
            />
        </div>
    );
};
